<script>
  import { Link } from 'svelte-routing';
  import { onMount } from 'svelte';
  import { theme, PALETTES } from '../lib/theme.svelte.js';
  import UpdateAvailable from './UpdateAvailable.svelte';

  let { user, logout, children } = $props();

  // Top destinations + a single bottom destination (Settings). Users is
  // not in the rail any more -- it lives as a sub-section inside Settings.
  // The active-title lookup uses both lists.
  const navTop = [
    { to: '/captures',    label: 'Captures',    icon: 'inbox' },
    { to: '/buckets',     label: 'Buckets',     icon: 'folder' },
    { to: '/logs',        label: 'Logs',        icon: 'manage_search' },
    { to: '/email-logs',  label: 'Email Logs',  icon: 'outgoing_mail' },
    { to: '/smokescreen', label: 'Smokescreen', icon: 'analytics' },
    { to: '/status',      label: 'Status',      icon: 'monitor_heart' },
    { to: '/sftp',        label: 'SFTP',        icon: 'folder_shared' },
    { to: '/audit',       label: 'Audit',       icon: 'history' },
  ];
  const navBottom = [
    { to: '/settings',    label: 'Settings',    icon: 'settings' },
  ];
  // Title lookup keeps Users mapped to "Users" even though it's not in the rail.
  const allTitles = [
    ...navTop, ...navBottom,
    { to: '/users', label: 'Users', icon: 'group' },
  ];

  // Mobile drawer + profile menu state.
  let drawerOpen = $state(false);
  let profileOpen = $state(false);
  function closeDrawer() { drawerOpen = false; }

  // Track active path for the mobile top-bar title and for hover/active CSS
  // (svelte-routing's isCurrent handles the link class, but we also want the
  // title to update without a popstate). Wrap pushState/replaceState so
  // every navigation surface here.
  let currentPath = $state(typeof window !== 'undefined' ? window.location.pathname : '/');
  function syncPath() { currentPath = window.location.pathname; }
  onMount(() => {
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...a) { const r = origPush.apply(this, a); syncPath(); return r; };
    history.replaceState = function (...a) { const r = origReplace.apply(this, a); syncPath(); return r; };
    window.addEventListener('popstate', syncPath);
    syncPath();
    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener('popstate', syncPath);
    };
  });
  const pageTitle = $derived(
    [...allTitles].sort((a, b) => b.to.length - a.to.length)
      .find((n) => currentPath === n.to || currentPath.startsWith(n.to + '/'))?.label || 'hook-index'
  );

  // svelte-routing's isCurrent is an EXACT path match, so a detail route like
  // /sftp/test left the whole rail unlit — nothing told you which section you
  // were in. pageTitle above already prefix-matches (that is why the title kept
  // working on deep routes); the highlight has to use the same rule or the two
  // disagree about where you are.
  //
  // Safe as a prefix test because no rail item is '/' — one would match every
  // route. The '/' + guard is what stops /sftp lighting up for /sftp-other.
  function isSectionActive(href) {
    if (!href || href === '/') return currentPath === href;
    return currentPath === href || currentPath.startsWith(href + '/');
  }

  function linkProps({ href, isCurrent }) {
    const active = isCurrent || isSectionActive(href);
    return { class: 'app-rail-link' + (active ? ' app-rail-link-active' : '') };
  }

  // Theme: mode (light/dark/system) + 16-color palette, owned by the shared
  // theme module so the choice persists and applies app-wide.
  const MODES = [
    { id: 'light',  label: 'Light',  icon: 'light_mode' },
    { id: 'dark',   label: 'Dark',   icon: 'dark_mode' },
    { id: 'system', label: 'System', icon: 'desktop_windows' },
  ];

  const displayName = $derived(
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.username || user?.email || 'Account'
  );
  const initials = $derived(
    ((user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')).toUpperCase() ||
    (user?.email?.[0] || '?').toUpperCase()
  );

  // Outside-click / Escape close.
  function onWindowClick(e) {
    if (profileOpen && !e.target.closest('.app-profile')) profileOpen = false;
  }
  function onKey(e) { if (e.key === 'Escape') { profileOpen = false; drawerOpen = false; } }
</script>

<svelte:window onclick={onWindowClick} onkeydown={onKey} />

<div class="app-shell" class:drawer-open={drawerOpen}>
  <!-- Navigation: a narrow M3 Navigation Rail on tablet/desktop, an
       off-canvas Navigation Drawer on phones. Same DOM; CSS reshapes per
       viewport. -->
  <aside class="app-rail">
    <!-- Mobile-only header inside the drawer: a brand row + close button. -->
    <div class="app-rail-mobilehead">
      <span class="app-rail-brand">
        <span class="material-symbols-outlined" style="color: rgb(var(--md-sys-color-primary));">webhook</span>
        <span>hook-index</span>
      </span>
      <button class="app-rail-close" onclick={closeDrawer} aria-label="Close menu">
        <span class="material-symbols-outlined">close</span>
      </button>
    </div>

    <nav class="app-rail-nav" aria-label="Primary">
      {#each navTop as item}
        <Link to={item.to} getProps={linkProps} onclick={closeDrawer}>
          <span class="app-rail-indicator">
            <span class="material-symbols-outlined app-rail-icon">{item.icon}</span>
          </span>
          <span class="app-rail-label">{item.label}</span>
        </Link>
      {/each}
    </nav>

    <!-- Bottom-pinned destinations (Settings). On desktop this sits flush
         at the bottom of the rail; on mobile drawer it's at the end of the
         scrolling list. -->
    <nav class="app-rail-nav app-rail-nav-bottom" aria-label="Secondary">
      {#each navBottom as item}
        <Link to={item.to} getProps={linkProps} onclick={closeDrawer}>
          <span class="app-rail-indicator">
            <span class="material-symbols-outlined app-rail-icon">{item.icon}</span>
          </span>
          <span class="app-rail-label">{item.label}</span>
        </Link>
      {/each}
    </nav>
  </aside>

  <!-- Scrim behind the mobile drawer. -->
  <button class="app-scrim" onclick={closeDrawer} aria-label="Close menu" tabindex="-1"></button>

  <!-- Main column: top bar (page title left, avatar right) + scrolling content. -->
  <div class="app-main-col">
    <header class="app-topbar">
      <button class="app-menu-btn app-menu-btn-mobile" onclick={() => (drawerOpen = true)} aria-label="Open menu">
        <span class="material-symbols-outlined">menu</span>
      </button>
      <span class="app-topbar-title">{pageTitle}</span>

      <div class="app-profile">
        <button class="app-profile-avatar-btn" onclick={(e) => { e.stopPropagation(); profileOpen = !profileOpen; }} aria-expanded={profileOpen} aria-label="Account">
          <span class="app-profile-initials">{initials}</span>
        </button>
        {#if profileOpen}
          <div class="app-profile-menu" role="menu">
            <div class="app-profile-menu-head">
              <div class="app-profile-menu-name">{displayName}</div>
              {#if user?.email && user.email !== displayName}<div class="app-profile-menu-email">{user.email}</div>{/if}
            </div>
            <div class="app-profile-divider"></div>
            <div class="app-profile-section">Mode</div>
            <div class="app-mode-seg">
              {#each MODES as m}
                <button
                  class="app-mode-btn"
                  class:selected={theme.mode === m.id}
                  onclick={() => theme.setMode(m.id)}
                  title={m.label}
                  aria-pressed={theme.mode === m.id}
                >
                  <span class="material-symbols-outlined">{m.icon}</span>
                  {m.label}
                </button>
              {/each}
            </div>
            <div class="app-profile-section">Color</div>
            <div class="app-palette-grid">
              {#each PALETTES as p}
                <button
                  class="app-palette-dot"
                  class:selected={theme.palette === p.name}
                  style:background={p.swatch}
                  onclick={() => theme.setPalette(p.name)}
                  title={p.label}
                  aria-label={p.label}
                  aria-pressed={theme.palette === p.name}
                >
                  {#if theme.palette === p.name}<span class="material-symbols-outlined">check</span>{/if}
                </button>
              {/each}
            </div>
            <div class="app-profile-divider"></div>
            <button class="app-profile-item" onclick={logout} role="menuitem">
              <span class="material-symbols-outlined">logout</span> Sign out
            </button>
          </div>
        {/if}
      </div>
    </header>

    <main class="app-main">
      {@render children?.()}
    </main>

    <!-- Offers a reload when the deployed build changes under an open tab. -->
    <UpdateAvailable />
  </div>
</div>

<style>
  /* Shell owns the viewport: only the main column scrolls. */
  .app-shell {
    block-size: 100vh;
    block-size: 100dvh;
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
  }

  /* ------------------------------------------------------------------
     MOBILE DEFAULTS: navigation drawer.
     The aside is an off-canvas drawer (transform when closed). Nav items
     are wide pill rows (icon left, label right). Profile-at-top.
     ------------------------------------------------------------------ */
  .app-rail {
    display: flex;
    flex-direction: column;
    background: rgb(var(--md-sys-color-surface));
    position: fixed;
    inset-block: 0;
    inset-inline-start: 0;
    inline-size: 320px;
    max-inline-size: 86vw;
    z-index: 40;
    transform: translateX(-100%);
    transition: transform 220ms cubic-bezier(.4, 0, .2, 1);
  }
  .drawer-open .app-rail { transform: translateX(0); }

  .app-rail-mobilehead {
    display: flex;
    align-items: center;
    justify-content: space-between;
    block-size: 56px;
    padding-inline: 16px 8px;
    flex-shrink: 0;
  }
  .app-rail-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 0.95rem;
  }
  .app-rail-close {
    inline-size: 40px;
    block-size: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    cursor: pointer;
  }
  .app-rail-close:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }

  /* Profile button (top-right of the top bar). */
  .app-profile { position: relative; }
  .app-profile-avatar-btn {
    inline-size: 40px;
    block-size: 40px;
    border-radius: 50%;
    border: 0;
    background: rgb(var(--md-sys-color-primary));
    color: rgb(var(--md-sys-color-on-primary));
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: box-shadow 120ms;
  }
  .app-profile-avatar-btn:hover {
    box-shadow: 0 0 0 4px rgb(var(--md-sys-color-on-surface) / 0.08);
  }
  .app-profile-initials {
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  /* Mobile nav: wide pill rows. Bottom nav (Settings) is pinned at the
     end of the scrolling area with a top margin pushing it down. */
  .app-rail-nav {
    flex: 1;
    min-block-size: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 8px 12px;
  }
  .app-rail-nav-bottom {
    flex: 0 0 auto;
    overflow: visible;
    padding: 0 12px 16px;
  }
  :global(.app-rail-link) {
    display: flex;
    align-items: center;
    block-size: 56px;
    gap: 12px;
    padding-inline: 16px 24px;
    border-radius: 28px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    text-decoration: none;
    font-size: 0.875rem;
    font-weight: 500;
    transition: background 120ms, color 120ms;
  }
  :global(.app-rail-link:hover) {
    background: rgb(var(--md-sys-color-on-surface) / 0.08);
    color: rgb(var(--md-sys-color-on-surface));
  }
  :global(.app-rail-link-active) {
    background: rgb(var(--md-sys-color-primary-container));
    color: rgb(var(--md-sys-color-on-primary-container));
  }
  :global(.app-rail-link-active .app-rail-icon) {
    color: rgb(var(--md-sys-color-on-primary-container));
  }
  /* The indicator is a pass-through wrapper on mobile (the link is the pill). */
  :global(.app-rail-indicator) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .app-rail-icon {
    font-size: 24px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  :global(.app-rail-label) {
    font-size: 0.875rem;
    font-weight: 500;
  }

  /* Profile menu (popup) -- anchored under the avatar at top-right. */
  .app-profile-menu {
    position: absolute;
    inset-block-start: calc(100% + 8px);
    inset-inline-end: 0;
    inline-size: 272px;
    z-index: 60;
    background: rgb(var(--md-sys-color-surface-container-high));
    border-radius: 12px;
    padding: 8px;
    box-shadow:
      0 2px 6px rgb(0 0 0 / 0.10),
      0 8px 24px rgb(0 0 0 / 0.14);
  }
  .app-profile-menu-head { padding: 8px 12px 4px; }
  .app-profile-menu-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: rgb(var(--md-sys-color-on-surface));
    line-height: 1.2;
  }
  .app-profile-menu-email {
    font-size: 0.75rem;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin-block-start: 2px;
  }
  .app-profile-section {
    font-size: 0.7rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: rgb(var(--md-sys-color-on-surface-variant));
    padding: 8px 12px 4px;
  }
  .app-profile-item {
    inline-size: 100%;
    block-size: 44px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding-inline: 12px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface));
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    text-align: start;
  }
  .app-profile-item:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .app-profile-item .material-symbols-outlined {
    font-size: 20px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    flex-shrink: 0;
  }
  .app-profile-item.selected {
    background: rgb(var(--md-sys-color-primary-container));
    color: rgb(var(--md-sys-color-on-primary-container));
  }
  .app-profile-item.selected .material-symbols-outlined {
    color: rgb(var(--md-sys-color-on-primary-container));
  }
  .app-profile-check { margin-inline-start: auto; font-size: 18px !important; }
  .app-profile-divider {
    block-size: 1px;
    background: rgb(var(--md-sys-color-outline-variant));
    margin: 6px 4px;
  }

  /* Mode segmented control: three equal pills (Light / Dark / System). */
  .app-mode-seg {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
    padding: 4px 8px 8px;
  }
  .app-mode-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 8px 4px;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 12px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    font-size: 0.72rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 120ms, color 120ms, border-color 120ms;
  }
  .app-mode-btn .material-symbols-outlined { font-size: 20px; }
  .app-mode-btn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.06); }
  .app-mode-btn.selected {
    background: rgb(var(--md-sys-color-primary-container));
    color: rgb(var(--md-sys-color-on-primary-container));
    border-color: transparent;
  }

  /* Palette grid: 16 color dots, 8 per row. Selected shows a check. */
  .app-palette-grid {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 6px;
    padding: 4px 8px 8px;
  }
  .app-palette-dot {
    aspect-ratio: 1;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    box-shadow: inset 0 0 0 1px rgb(0 0 0 / 0.12);
    transition: transform 120ms;
  }
  .app-palette-dot:hover { transform: scale(1.15); }
  .app-palette-dot.selected {
    box-shadow: 0 0 0 2px rgb(var(--md-sys-color-surface-container-high)),
                0 0 0 4px rgb(var(--md-sys-color-on-surface));
  }
  .app-palette-dot .material-symbols-outlined {
    font-size: 14px;
    text-shadow: 0 0 2px rgb(0 0 0 / 0.5);
  }

  /* Scrim (mobile only). */
  .app-scrim {
    position: fixed;
    inset: 0;
    z-index: 30;
    border: 0;
    background: rgb(0 0 0 / 0.4);
    opacity: 0;
    pointer-events: none;
    transition: opacity 220ms;
    cursor: pointer;
  }
  .drawer-open .app-scrim { opacity: 1; pointer-events: auto; }

  /* Main column. Sits on the bright `surface` so the inset rounded content
     panel reads as a card floating on it. */
  .app-main-col {
    display: flex;
    flex-direction: column;
    min-inline-size: 0;
    min-block-size: 0;
    background: rgb(var(--md-sys-color-surface));
  }
  /* Top app bar: always visible (mobile + desktop). Page title left,
     avatar right. Same surface as the rail; the content area below uses
     a tinted background to create the visual separation -- no line. */
  .app-topbar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    block-size: 64px;
    padding-inline: 12px 16px;
    background: rgb(var(--md-sys-color-surface));
    flex-shrink: 0;
    position: relative;
    z-index: 10;
  }
  .app-menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    inline-size: 44px;
    block-size: 44px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface));
    cursor: pointer;
  }
  .app-menu-btn:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .app-topbar-title {
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 0.006em;
    flex: 1;
    min-inline-size: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Content panel: a rounded surface-container-high card inset from the
     rail+topbar (which sit on bright `surface`). The tone step plus the
     rounded corners read as an M3 content pane. On mobile it's flush
     (no rail), so only round the top corners there. */
  .app-main {
    flex: 1;
    min-block-size: 0;
    overflow-y: auto;
    inline-size: auto;
    padding: 1rem;
    margin: 0 8px 8px;
    background: rgb(var(--md-sys-color-surface-container-high));
    border-radius: 16px 16px 0 0;
  }

  /* ------------------------------------------------------------------
     TABLET / DESKTOP: M3 Navigation Rail
     80dp wide rail; each destination is a vertical stack of indicator
     (56x32 pill around icon) + label below. Profile becomes a small
     circular avatar button at the bottom of the rail.
     ------------------------------------------------------------------ */
  @media (min-width: 600px) {
    /* M3 rail width (80dp). Keeps the right gutter tight against the card. */
    .app-shell { grid-template-columns: 84px 1fr; }

    .app-rail {
      position: relative;
      inset: auto;
      transform: none;
      inline-size: 84px;
      max-inline-size: none;
      /* Rail keeps the brightest surface; the content area below uses a
         tinted background so the rail/topbar separate by tone, no line. */
      background: rgb(var(--md-sys-color-surface));
      z-index: auto;
      align-items: stretch;
    }
    .app-rail-mobilehead { display: none; }
    /* Hamburger button is only for the mobile drawer. */
    .app-menu-btn-mobile { display: none; }

    /* Rail nav: vertical icon+label stacks. Top group scrolls if needed,
       bottom group (Settings) is pinned flush at the bottom. scrollbar-gutter
       is avoided -- overflow stays hidden unless it truly overflows so no
       scrollbar reserves space on the right (which made items look
       off-center toward the left). */
    .app-rail-nav {
      flex: 1;
      min-block-size: 0;
      overflow-y: auto;
      overflow-x: hidden;
      scrollbar-width: none;
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 8px 0;
      align-items: center;
    }
    .app-rail-nav::-webkit-scrollbar { display: none; }
    .app-rail-nav-bottom {
      flex: 0 0 auto;
      padding: 8px 0 16px;
    }
    /* M3 Expressive: larger active indicator (64x40), softer "extra-large"
       corner radius (20dp), bolder active label, springier transition. */
    :global(.app-rail-link) {
      inline-size: 72px;
      block-size: auto;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
      padding: 6px 4px 10px;
      border-radius: 16px;
      background: transparent !important;
      color: rgb(var(--md-sys-color-on-surface-variant));
    }
    :global(.app-rail-link:hover) {
      background: transparent !important;
      color: rgb(var(--md-sys-color-on-surface));
    }
    /* Hover state layer applies ONLY to inactive items, otherwise the
       active primary-container fill would briefly flip to a neutral tint
       on mouseover (the "blink"). Active items keep their fill on hover. */
    :global(.app-rail-link:not(.app-rail-link-active):hover .app-rail-indicator) {
      background: rgb(var(--md-sys-color-on-surface) / 0.10);
    }
    :global(.app-rail-indicator) {
      inline-size: 64px;
      block-size: 40px;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* Expressive emphasized spring -- a touch of overshoot. */
      transition: background 240ms cubic-bezier(.34, 1.56, .64, 1),
                  transform 240ms cubic-bezier(.34, 1.56, .64, 1);
    }
    :global(.app-rail-link-active) {
      color: rgb(var(--md-sys-color-on-surface));
    }
    :global(.app-rail-link-active .app-rail-indicator) {
      background: rgb(var(--md-sys-color-primary-container));
    }
    :global(.app-rail-link-active .app-rail-icon) {
      color: rgb(var(--md-sys-color-on-primary-container));
      /* Expressive: filled-style icon for active state. */
      font-variation-settings: 'FILL' 1, 'wght' 500;
    }
    :global(.app-rail-label) {
      font-size: 0.72rem;
      font-weight: 500;
      line-height: 1.1;
      text-align: center;
      letter-spacing: 0.03em;
      color: rgb(var(--md-sys-color-on-surface-variant));
      transition: color 160ms, font-weight 160ms;
    }
    :global(.app-rail-link-active .app-rail-label) {
      color: rgb(var(--md-sys-color-on-surface));
      font-weight: 600;
    }

    .app-scrim { display: none; }
    /* Desktop: rounded content pane. The rail already provides its own
       internal gutter, so the card hugs it with only a hairline gap on the
       left; the surface shows more generously on the right/bottom. */
    .app-main {
      padding: 1.5rem 1.75rem;
      margin: 4px 12px 12px 4px;
      border-radius: 24px;
    }
    /* Align the top-bar title with the card's content inset (card left
       margin 4px + card padding 1.75rem = ~32px) so the page title lines
       up with the content below it. */
    .app-topbar { padding-inline-start: calc(4px + 1.75rem); }
  }
</style>
